/* The privacy policy's and the terms' shared page: Legal's layout, sections as data.
 *
 * Stripe's live account links both pages, so they must answer to anybody, signed in or
 * not, with esback or LaunchDarkly down. Nothing here fetches, reads a flag, or asks who
 * is signed in, and index.js routes both bare (EsPolicyRoutes.test.jsx holds it to that).
 * The copy is literal, as Legal's is: a translated policy is a second policy to keep true.
 */

import { Link } from 'react-router-dom';
import { esDayMonYear } from '../../common/EsDate';
import { useEsCopy } from '../../common/EsCopy';
import { useEsTranslationsFlag } from '../../common/EsFlags';
import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';

// Both pages say what they say as of this day. A change to either moves it.
export const POLICY_EFFECTIVE = '2026-10-30';

// The legal name (Tim, 2026-10-14).
export const BUSINESS_NAME = 'Alder Loons';

// The reply-to every mail from escapade.gg carries; the domain's inbound is a catch-all.
export const CONTACT_EMAIL = 'contact@escapade.gg';

export const LINK_CLASS = 'underline decoration-dotted hover:text-accent-hover';

export const EsP = ( { children } ) => <p className="mb-4">{ children }</p>;

export const EsList = ( { children } ) => <ul className="mb-4 list-disc pl-5 space-y-1">{ children }</ul>;

export const EsMail = () => <a href={ 'mailto:' + CONTACT_EMAIL } className={ LINK_CLASS }>{ CONTACT_EMAIL }</a>;

export const EsTo = ( { to, children } ) => <Link to={ to } className={ LINK_CLASS }>{ children }</Link>;

/**
 * Legal text is English only (es-i18n.md, Decisions: Legal). A reader in another locale is told
 * so, in their language, rather than handed a translation that would bind nobody. Nothing while
 * copykeys is off or the reader reads en-US.
 *
 * @param id        the testid stem; the line is <id>-english-governs
 * @param className its spacing where it stands
 */
export function EsEnglishGoverns( { id, className = 'mb-10 text-center' } ) {
    const t = useEsStrings();
    const aCopy = useEsCopy();
    const aForeign = useEsTranslationsFlag() && aCopy.loaded && Boolean( aCopy.group ) && aCopy.group !== 'en-US';
    if ( !aForeign ) return null;
    return (
        <p data-testid={ id + '-english-governs' } className={ className }>
            { t( 'legal-english-governs', 'This page is in English, and the English text governs.' ) }
        </p>
    );
}

/**
 * One policy page: heading, effective date, then each section as a heading and its body.
 *
 * @param id       the testid stem, es-privacy or es-terms
 * @param title    the page heading
 * @param sections [ { key, heading, body } ], drawn in order
 */
export default function EsPolicy( { id, title, sections } ) {
    const t = useEsStrings();
    return (
        <section data-testid={ id + '-page' } className="relative z-10 pb-4 pt-nav-room">
            <EsMinis />
            <div className="container">
                <div className="mx-auto px-4 xl:w-[800px] max-sm:break-words">
                    <h3 data-testid={ id + '-heading' } className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
                        { title }
                    </h3>
                    <p data-testid={ id + '-effective' } className="mb-10 text-center opacity-70">
                        { t( 'legal-effective', 'Effective {date}', { date: esDayMonYear( POLICY_EFFECTIVE ) } ) }
                    </p>
                    <EsEnglishGoverns id={ id } />
                    { sections.map( ( pSection ) => (
                        <div key={ pSection.key }>
                            <h4 data-testid={ id + '-' + pSection.key + '-heading' } className="mb-3 text-lg font-bold text-heading">
                                { pSection.heading }
                            </h4>
                            <div data-testid={ id + '-' + pSection.key } className="mb-6 text-base leading-8">
                                { pSection.body }
                            </div>
                        </div>
                    ) ) }
                </div>
            </div>
        </section>
    );
}
